import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';

/**
 * Pone la identidad de la tienda en la pestana del navegador: titulo y favicon.
 *
 * Funciona en cliente, que basta para la pestana porque la pinta el navegador ejecutando
 * nuestro JS. NO basta para las vistas previas al compartir enlaces: los rastreadores de
 * WhatsApp, Facebook, Twitter y LinkedIn leen el HTML crudo sin ejecutar scripts, asi que
 * nunca ven nada de lo que se fije aqui. Eso hay que inyectarlo aguas arriba, en el worker
 * de Cloudflare que ya hace de proxy de cada dominio de tienda.
 */
@Injectable({ providedIn: 'root' })
export class StoreBrandingService {
  constructor(
    private title: Title,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  apply(storeName?: string | null, logoUrl?: string | null): void {
    this.applyTitle(storeName);
    this.applyFavicon(storeName, logoUrl);
  }

  private applyTitle(storeName?: string | null): void {
    const name = storeName?.trim();
    if (name) {
      this.title.setTitle(name);
    }
  }

  private applyFavicon(storeName?: string | null, logoUrl?: string | null): void {
    const href = this.resolveHref(storeName, logoUrl);
    if (!href) return;

    // index.html trae <link id="appIcon" href="">, asi que se reutiliza si existe.
    let link = this.document.getElementById('appIcon') as HTMLLinkElement | null;
    if (!link) {
      link = this.document.createElement('link');
      link.id = 'appIcon';
      link.rel = 'icon';
      this.document.head.appendChild(link);
    }
    link.type = href.startsWith('data:image/svg') ? 'image/svg+xml' : 'image/x-icon';
    link.href = href;
  }

  /** El logo de la tienda si lo hay; si no, un monograma con su inicial. */
  private resolveHref(storeName?: string | null, logoUrl?: string | null): string | null {
    const logo = logoUrl?.trim();
    if (logo) {
      return logo;
    }
    return this.monogram(storeName);
  }

  /** Primera letra del nombre sobre el color de tema, como SVG en linea. */
  monogram(storeName?: string | null): string | null {
    const letter = this.firstLetter(storeName);
    if (!letter) return null;

    // --theme-color lo define la hoja de estilos del tema que inyecta LayoutComponent,
    // asi que el icono acompana al tema activo de la tienda.
    const themeColor =
      getComputedStyle(this.document.documentElement)
        .getPropertyValue('--theme-color')
        .trim() || '#1F2937';

    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">` +
      `<rect width="32" height="32" rx="7" fill="${this.escapeXml(themeColor)}"/>` +
      `<text x="16" y="17" fill="#fff" font-family="system-ui,-apple-system,Segoe UI,Roboto,sans-serif" ` +
      `font-size="18" font-weight="700" text-anchor="middle" dominant-baseline="central">` +
      `${this.escapeXml(letter)}</text></svg>`;

    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }

  /** Salta puntuacion y emojis, y admite alfabetos no latinos. */
  private firstLetter(storeName?: string | null): string | null {
    const name = storeName?.trim();
    if (!name) return null;
    const match = Array.from(name).find((char) => /\p{L}|\p{N}/u.test(char));
    return match ? match.toLocaleUpperCase() : null;
  }

  private escapeXml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
